import { ServiceStatus } from '../../types';
import { statusLabels } from '../../utils/status';

/**
 * Status as a filled chip rather than a dot beside plain text.
 *
 * The dot alone repeated the label; this carries the state in shape and tint
 * as well as colour, so it survives greyscale and colour-blind viewing.
 */
const tones: Record<ServiceStatus, string> = {
  up:          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  down:        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  degraded:    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  maintenance: 'bg-brand-100 text-brand-800 dark:bg-brand-900/30 dark:text-brand-300',
  pending:     'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

/** Short forms so the pill stays compact next to a service name. */
const shortLabels: Partial<Record<ServiceStatus, string>> = {
  up: 'Up',
  maintenance: 'Maint',
};

interface Props {
  status: ServiceStatus;
  /** Use the full wording ("Operational") instead of the compact form. */
  full?: boolean;
}

export function StatusPill({ status, full = false }: Props) {
  const label = full ? statusLabels[status] : shortLabels[status] ?? statusLabels[status];

  return (
    <span
      className={`inline-flex items-center gap-1.5 shrink-0 rounded px-1.5 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wide ${tones[status]}`}
    >
      <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}
